import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import axios from "axios";
import styles from "../../../styles/styles";
import ServiceCard from "../../Services/ServiceCard";
import { server } from "../../../server";

const getServicePrice = (service = {}) => {
  const price = Number(
    service?.discountPrice ?? service?.originalPrice ?? service?.price ?? 0
  );
  return Number.isFinite(price) ? price : 0;
};

const getActiveServices = (services = []) => {
  return (services || [])
    .filter((item) => {
      if (item?.status && item.status !== "active") return false;

      // Many of your service/event models use approvedEnd as the expiry
      if (item?.approvedEnd) {
        const end = new Date(item.approvedEnd);
        if (end < new Date()) return false;
      }

      return true;
    })
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
};

const ServicesSlider = ({ config = {} }) => {
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const sliderRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const loadServices = async () => {
      try {
        setIsLoading(true);
        const { data } = await axios.get(`${server}/service/get-all-events`, {
          withCredentials: true,
        });
        if (cancelled) return;
        setServices(data?.events || []);
      } catch {
        if (cancelled) return;
        setServices([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadServices();
    return () => {
      cancelled = true;
    };
  }, []);

  const visibleCards = useMemo(() => {
    const isMobile = typeof window !== "undefined" ? window.innerWidth < 640 : false;
    const raw = config?.productsToShow ?? 5;
    const productsToShow = Math.min(8, Math.max(1, Number(raw) || 5));
    return { isMobile, productsToShow };
  }, [config?.productsToShow]);

  const autoplayEnabled = config?.autoplay === true;
  const autoplaySpeed = config?.autoplaySpeed || 3000;
  const showArrows = config?.showArrows !== false;

  const servicesToDisplay = useMemo(() => {
    const limit = typeof config?.limit === "number" && config.limit >= 0 ? config.limit : 12;
    const active = getActiveServices(services);

    const sortBy = config?.sortBy || "newest";
    let sorted = active;

    if (sortBy === "priceLow") {
      sorted = [...active].sort((a, b) => getServicePrice(a) - getServicePrice(b));
    } else if (sortBy === "priceHigh") {
      sorted = [...active].sort((a, b) => getServicePrice(b) - getServicePrice(a));
    } else {
      // newest already applied in getActiveServices()
      sorted = active;
    }

    const cap = typeof limit === "number" && limit > 0 ? limit : 12;
    return sorted.slice(0, cap);
  }, [services, config?.limit, config?.sortBy]);

  const gap = 24;
  const productsToShow = visibleCards.isMobile ? 2.2 : visibleCards.productsToShow;

  const scrollToIndex = useCallback(
    (index) => {
      if (!sliderRef.current || !servicesToDisplay.length) return;
      const container = sliderRef.current;
      const card = container.querySelector("[data-service-card]");
      if (!card) return;

      const cardWidth = card.offsetWidth + gap;
      const maxIndex = Math.max(0, servicesToDisplay.length - Math.ceil(productsToShow));
      const clamped = Math.max(0, Math.min(index, maxIndex));

      container.scrollTo({ left: clamped * cardWidth, behavior: "smooth" });
      setCurrentIndex(clamped);
    },
    [servicesToDisplay.length, productsToShow]
  );

  useEffect(() => {
    if (!autoplayEnabled || !servicesToDisplay.length) return;
    if (servicesToDisplay.length <= Math.ceil(productsToShow)) return;

    const id = setInterval(() => {
      setCurrentIndex((prev) => {
        const maxIndex = Math.max(0, servicesToDisplay.length - Math.ceil(productsToShow));
        const next = prev >= maxIndex ? 0 : prev + 1;
        if (sliderRef.current) {
          const container = sliderRef.current;
          const card = container.querySelector("[data-service-card]");
          if (card) {
            const cardWidth = card.offsetWidth + gap;
            container.scrollTo({ left: next * cardWidth, behavior: "smooth" });
          }
        }
        return next;
      });
    }, autoplaySpeed);

    return () => clearInterval(id);
  }, [autoplayEnabled, autoplaySpeed, servicesToDisplay.length, productsToShow, scrollToIndex]);

  const handleScroll = useCallback(() => {
    if (!sliderRef.current) return;
    const container = sliderRef.current;
    const card = container.querySelector("[data-service-card]");
    if (!card) return;
    const cardWidth = card.offsetWidth + gap;
    const index = Math.round(container.scrollLeft / cardWidth);
    setCurrentIndex(Math.max(0, Math.min(index, servicesToDisplay.length - 1)));
  }, [servicesToDisplay.length]);

  const handlePrev = () => scrollToIndex(currentIndex - 1);
  const handleNext = () => scrollToIndex(currentIndex + 1);

  const viewAllLink = config.viewAllLink || "/services";
  const buttonText = config.buttonText || "View All";

  if (isLoading) return null;

  const hasServices = servicesToDisplay.length > 0;
  const showNav = servicesToDisplay.length > Math.ceil(productsToShow);

  return (
    <div className="mb-12">
      <div className={`${styles.section}`}>
        <div className="flex justify-between items-center pb-5">
          <div>
            <h1 className={`${styles.heading} pb-0`}>
              {config.heading || "Services"}
            </h1>
            {config.subheading && (
              <p className="text-sm text-gray-500 mt-1">{config.subheading}</p>
            )}
          </div>

          {viewAllLink && (
            <Link to={viewAllLink}>
              <div className={`${styles.button} text-[#fff]`}>{buttonText}</div>
            </Link>
          )}
        </div>

        {hasServices ? (
          <div className="relative">
            <div
              ref={sliderRef}
              onScroll={handleScroll}
              className="w-full overflow-x-auto scrollbar-hide scroll-smooth snap-x snap-mandatory"
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                WebkitOverflowScrolling: "touch",
              }}
            >
              <div className="flex gap-6" style={{ gap: `${gap}px` }}>
                {servicesToDisplay.map((service) => (
                  <div
                    key={service._id}
                    data-service-card
                    className="flex-shrink-0 snap-start home-product-card"
                    style={{
                      width: `calc((100% - ${(productsToShow - 1) * gap}px) / ${productsToShow})`,
                      minWidth: `calc((100% - ${(productsToShow - 1) * gap}px) / ${productsToShow})`,
                    }}
                  >
                    <ServiceCard data={service} />
                  </div>
                ))}
              </div>
            </div>

            {showNav && showArrows && (
              <>
                <button
                  type="button"
                  onClick={handlePrev}
                  className="home-prod-arrow-left absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg border border-gray-200 transition-all hover:scale-110"
                  aria-label="Previous"
                >
                  <IoIosArrowBack size={24} />
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className="home-prod-arrow-right absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg border border-gray-200 transition-all hover:scale-110"
                  aria-label="Next"
                >
                  <IoIosArrowForward size={24} />
                </button>
              </>
            )}
          </div>
        ) : (
          <p className="text-center text-gray-500 py-8">
            No services at the moment.
          </p>
        )}
      </div>
    </div>
  );
};

export default ServicesSlider;

